import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import { FilterChip } from '@/shared/components/ui/FilterChip';

// ═══════════════════════════════════════════════════════
// EXPORTED INTERFACES
// ═══════════════════════════════════════════════════════

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDefinition {
  key: string;
  label: string;
  type: 'multiselect' | 'text' | 'daterange';
  options?: FilterOption[];
  placeholder?: string;
  dynamic?: boolean;
}

export interface FilterCategory {
  id: string;
  label: string;
  filters: string[];
}

export interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
}

export interface AdvancedFiltersBarProps {
  /** Filter definitions keyed by filter key */
  definitions: Record<string, FilterDefinition>;
  /** Categories that group filters in the popover */
  categories: FilterCategory[];
  /** Current active filters */
  filters: Record<string, unknown>;
  /** Callback when filters change */
  onFiltersChange: (filters: Record<string, unknown>) => void;
  /** Dynamic options for filters with dynamic: true */
  dynamicOptions?: Record<string, FilterOption[]>;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function formatDate(dateStr: string): string {
  try {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) return `${day}/${month}`;
    return dateStr;
  } catch {
    return dateStr;
  }
}

/**
 * Compute list of active filters with display labels.
 * Iterates over definitions (not hardcoded keys) for full genericity.
 */
export function getActiveFilters(
  filters: Record<string, unknown>,
  definitions: Record<string, FilterDefinition>,
  dynamicOptions?: Record<string, FilterOption[]>
): ActiveFilter[] {
  const entries: ActiveFilter[] = [];

  for (const [key, def] of Object.entries(definitions)) {
    if (def.type === 'daterange') continue; // handled separately below

    const value = filters[key];
    if (value === undefined || value === null || value === '') continue;

    if (def.type === 'multiselect') {
      const values = Array.isArray(value) ? value : [value];
      if (values.length === 0) continue;

      const options =
        def.dynamic && dynamicOptions?.[key]
          ? dynamicOptions[key]
          : def.options || [];

      const labels = (values as string[])
        .map((v) => options.find((o) => o.value === v)?.label || v)
        .filter(Boolean);

      if (labels.length === 0) continue;

      let displayValue: string;
      if (labels.length <= 2) displayValue = labels.join(', ');
      else displayValue = `${labels[0]}, +${labels.length - 1}`;

      entries.push({ key, label: def.label, displayValue });
    } else if (def.type === 'text') {
      entries.push({ key, label: def.label, displayValue: String(value) });
    }
  }

  // Date range (composite chip using dateFrom/dateTo keys)
  const dateFrom = filters.dateFrom as string | undefined;
  const dateTo = filters.dateTo as string | undefined;
  if (dateFrom || dateTo) {
    const fromStr = dateFrom ? formatDate(dateFrom) : '';
    const toStr = dateTo ? formatDate(dateTo) : '';
    let displayValue = '';
    if (fromStr && toStr) displayValue = `${fromStr} - ${toStr}`;
    else if (fromStr) displayValue = `a partir de ${fromStr}`;
    else if (toStr) displayValue = `até ${toStr}`;

    // Find the daterange definition label (fallback to 'Período')
    const dateRangeDef = Object.values(definitions).find((d) => d.type === 'daterange');
    entries.push({
      key: dateRangeDef?.key || 'dateRange',
      label: dateRangeDef?.label || 'Período',
      displayValue,
    });
  }

  return entries;
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

/**
 * Generic advanced filters bar with chip-based UI (Linear/Attio style).
 *
 * Active filters are displayed as clickable chips. New filters are added
 * via a popover with categories and value selectors.
 *
 * @example
 * ```tsx
 * <AdvancedFiltersBar
 *   definitions={LEADS_FILTER_DEFINITIONS}
 *   categories={LEADS_FILTER_CATEGORIES}
 *   filters={filters}
 *   onFiltersChange={setFilters}
 *   dynamicOptions={{ tags: tagOptions, origin: originOptions }}
 * />
 * ```
 */
export const AdvancedFiltersBar: React.FC<AdvancedFiltersBarProps> = ({
  definitions,
  categories,
  filters,
  onFiltersChange,
  dynamicOptions,
}) => {
  // ── Popover state ──
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'categories' | 'values'>('categories');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [optionSearchQuery, setOptionSearchQuery] = useState('');

  // ── Local values for value selector ──
  const [localValues, setLocalValues] = useState<string[] | string>([]);
  const [localDateFrom, setLocalDateFrom] = useState('');
  const [localDateTo, setLocalDateTo] = useState('');

  // ── Positioning ──
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // ── Merged filter definitions (static + dynamic options) ──
  const allFilterDefinitions = useMemo<Record<string, FilterDefinition>>(() => {
    if (!dynamicOptions || Object.keys(dynamicOptions).length === 0) return definitions;

    const merged = { ...definitions };
    for (const [key, options] of Object.entries(dynamicOptions)) {
      if (merged[key]) {
        merged[key] = { ...merged[key], options };
      }
    }
    return merged;
  }, [definitions, dynamicOptions]);

  // ── Derived state ──
  const activeFilters = useMemo(
    () => getActiveFilters(filters, allFilterDefinitions, dynamicOptions),
    [filters, allFilterDefinitions, dynamicOptions]
  );
  const hasFilters = activeFilters.length > 0;
  const currentFilterDef = selectedFilter ? allFilterDefinitions[selectedFilter] : null;

  // ── Update popover position ──
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  // ── Filtered categories for search ──
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;

    const query = searchQuery.toLowerCase();
    return categories
      .map((cat) => ({
        ...cat,
        filters: cat.filters.filter((fKey) => {
          const def = allFilterDefinitions[fKey];
          return def?.label.toLowerCase().includes(query);
        }),
      }))
      .filter((cat) => cat.filters.length > 0);
  }, [searchQuery, allFilterDefinitions, categories]);

  // ── Filtered options for multiselect ──
  const filteredOptions = useMemo(() => {
    if (!currentFilterDef?.options) return [];
    if (!optionSearchQuery.trim()) return currentFilterDef.options;
    const query = optionSearchQuery.toLowerCase();
    return currentFilterDef.options.filter((o) =>
      o.label.toLowerCase().includes(query)
    );
  }, [currentFilterDef, optionSearchQuery]);

  // ── Open popover: add new filter ──
  const handleOpenAdd = () => {
    setStep('categories');
    setSelectedFilter(null);
    setSearchQuery('');
    setOptionSearchQuery('');
    setLocalValues([]);
    setLocalDateFrom('');
    setLocalDateTo('');
    setIsOpen(true);
  };

  // ── Open popover: edit existing chip ──
  const handleEditChip = (filterKey: string) => {
    const def = allFilterDefinitions[filterKey];
    setStep('values');
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');

    if (def?.type === 'daterange') {
      setLocalDateFrom((filters.dateFrom as string) || '');
      setLocalDateTo((filters.dateTo as string) || '');
    } else if (def?.type === 'multiselect') {
      const value = filters[filterKey];
      setLocalValues(
        Array.isArray(value) ? [...value] : value ? [value as string] : []
      );
    } else {
      const value = filters[filterKey];
      setLocalValues((value as string) || '');
    }

    setIsOpen(true);
  };

  // ── Select filter from category menu ──
  const handleSelectCategory = (filterKey: string) => {
    const def = allFilterDefinitions[filterKey];
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');

    if (def?.type === 'daterange') {
      setLocalDateFrom((filters.dateFrom as string) || '');
      setLocalDateTo((filters.dateTo as string) || '');
    } else if (def?.type === 'multiselect') {
      const currentValue = filters[filterKey];
      setLocalValues(
        Array.isArray(currentValue)
          ? [...currentValue]
          : currentValue
            ? [currentValue as string]
            : []
      );
    } else {
      const currentValue = filters[filterKey];
      setLocalValues((currentValue as string) || '');
    }

    setStep('values');
  };

  // ── Apply selected values ──
  const handleApply = () => {
    if (!selectedFilter) return;

    const newFilters = { ...filters };
    const def = allFilterDefinitions[selectedFilter];

    if (def?.type === 'daterange') {
      if (localDateFrom) newFilters.dateFrom = localDateFrom;
      else delete newFilters.dateFrom;
      if (localDateTo) newFilters.dateTo = localDateTo;
      else delete newFilters.dateTo;
    } else {
      const isEmpty =
        localValues === undefined ||
        localValues === null ||
        localValues === '' ||
        (Array.isArray(localValues) && localValues.length === 0);

      if (isEmpty) {
        delete newFilters[selectedFilter];
      } else {
        newFilters[selectedFilter] = localValues;
      }
    }

    onFiltersChange(newFilters);
    setIsOpen(false);
  };

  // ── Remove a chip ──
  const handleRemoveChip = (filterKey: string) => {
    const newFilters = { ...filters };
    const def = allFilterDefinitions[filterKey];

    if (def?.type === 'daterange') {
      delete newFilters.dateFrom;
      delete newFilters.dateTo;
    } else {
      delete newFilters[filterKey];
    }

    onFiltersChange(newFilters);
  };

  // ── Clear all filters ──
  const handleClear = () => {
    onFiltersChange({});
  };

  // ── Toggle multiselect value ──
  const handleToggleOption = (value: string) => {
    if (!Array.isArray(localValues)) {
      setLocalValues([value]);
      return;
    }
    if (localValues.includes(value)) {
      setLocalValues(localValues.filter((v) => v !== value));
    } else {
      setLocalValues([...localValues, value]);
    }
  };

  // ── Close popover ──
  const handleClose = () => {
    setIsOpen(false);
  };

  // ── Back to categories ──
  const handleBack = () => {
    setStep('categories');
    setSelectedFilter(null);
    setSearchQuery('');
  };

  // ── Check if a filter is active ──
  const isFilterActive = (filterKey: string): boolean => {
    const def = allFilterDefinitions[filterKey];
    if (def?.type === 'daterange') {
      return !!(filters.dateFrom || filters.dateTo);
    }
    const value = filters[filterKey];
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* ═══ Active Filter Chips ═══ */}
      {activeFilters.map((filter) => (
        <FilterChip
          key={filter.key}
          label={filter.label}
          value={filter.displayValue}
          onClick={() => handleEditChip(filter.key)}
          onRemove={() => handleRemoveChip(filter.key)}
        />
      ))}

      {/* ═══ Add Filter Button ═══ */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpenAdd}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
        style={{
          backgroundColor: 'var(--theme-button-ghost)',
          color: 'var(--theme-header-text)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-header-button-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost)';
        }}
      >
        <Icon name="plus" className="w-3.5 h-3.5" />
        <span>Filtro</span>
      </button>

      {/* ═══ Clear Button ═══ */}
      {hasFilters && (
        <button
          type="button"
          onClick={handleClear}
          className="text-xs font-medium px-2 py-1.5 rounded transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--theme-header-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          Limpar
        </button>
      )}

      {/* ═══ Popover ═══ */}
      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[100]"
              onClick={handleClose}
            />

            {/* Panel */}
            <div
              className="fixed z-[101] w-72 rounded-lg shadow-xl flex flex-col"
              style={{
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                maxHeight: '420px',
              }}
            >
              {step === 'categories' ? (
                /* ═══════════════════════════════════════ */
                /* STEP 1: CATEGORY LIST                  */
                /* ═══════════════════════════════════════ */
                <>
                  {/* Search */}
                  <div
                    className="p-3 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <div className="relative">
                      <Icon
                        name="search"
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                        style={{ color: '#9ca3af' }}
                      />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar filtro..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border"
                        style={{
                          backgroundColor: '#f9fafb',
                          borderColor: '#e5e7eb',
                          color: '#1e293b',
                        }}
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Category Groups */}
                  <div className="overflow-y-auto flex-1 py-1">
                    {filteredCategories.length === 0 ? (
                      <div
                        className="text-center py-6 text-sm"
                        style={{ color: '#9ca3af' }}
                      >
                        Nenhum filtro encontrado
                      </div>
                    ) : (
                      filteredCategories.map((category, catIndex) => (
                        <div key={category.id}>
                          {/* Category Header */}
                          <div
                            className="flex items-center gap-2 px-3 py-1.5"
                            style={{
                              color: '#9ca3af',
                              ...(catIndex > 0
                                ? {
                                    borderTop: '1px solid #f3f4f6',
                                    marginTop: '4px',
                                    paddingTop: '8px',
                                  }
                                : {}),
                            }}
                          >
                            <span className="text-[11px] font-semibold uppercase tracking-wider">
                              {category.label}
                            </span>
                          </div>

                          {/* Filter Items */}
                          {category.filters.map((filterKey) => {
                            const def = allFilterDefinitions[filterKey];
                            if (!def) return null;

                            const active = isFilterActive(filterKey);

                            return (
                              <button
                                key={filterKey}
                                type="button"
                                onClick={() => handleSelectCategory(filterKey)}
                                className="w-full flex items-center justify-between py-2 text-sm transition-colors text-left"
                                style={{
                                  paddingLeft: '2rem',
                                  paddingRight: '0.75rem',
                                  color: '#1e293b',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <span>{def.label}</span>
                                {active && (
                                  <div
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: '#8b5cf6' }}
                                  />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                /* ═══════════════════════════════════════ */
                /* STEP 2: VALUE SELECTOR                 */
                /* ═══════════════════════════════════════ */
                <>
                  {/* Header with back button */}
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleBack}
                      className="p-1 rounded transition-colors"
                      style={{ color: '#64748b' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon name="chevron-left" className="w-4 h-4" />
                    </button>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: '#1e293b' }}
                    >
                      {currentFilterDef?.label}
                    </span>
                  </div>

                  {/* Value Content */}
                  <div className="overflow-y-auto flex-1">
                    {/* ── MultiSelect ── */}
                    {currentFilterDef?.type === 'multiselect' && (
                      <>
                        {/* Search (only if many options) */}
                        {(currentFilterDef.options?.length || 0) > 5 && (
                          <div className="px-3 pt-3 pb-1">
                            <div className="relative">
                              <Icon
                                name="search"
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                                style={{ color: '#9ca3af' }}
                              />
                              <input
                                type="text"
                                value={optionSearchQuery}
                                onChange={(e) =>
                                  setOptionSearchQuery(e.target.value)
                                }
                                placeholder={currentFilterDef.placeholder || 'Buscar...'}
                                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border"
                                style={{
                                  backgroundColor: '#f9fafb',
                                  borderColor: '#e5e7eb',
                                  color: '#1e293b',
                                }}
                                autoFocus
                              />
                            </div>
                          </div>
                        )}

                        {/* Options */}
                        <div className="p-2 space-y-0.5">
                          {filteredOptions.length === 0 ? (
                            <div
                              className="text-center py-4 text-sm"
                              style={{ color: '#9ca3af' }}
                            >
                              Nenhuma opção encontrada
                            </div>
                          ) : (
                            filteredOptions.map((option) => {
                              const isSelected =
                                Array.isArray(localValues) &&
                                localValues.includes(option.value);

                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleToggleOption(option.value)}
                                  className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
                                  style={{
                                    backgroundColor: isSelected
                                      ? '#f5f3ff'
                                      : 'transparent',
                                    color: '#1e293b',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected)
                                      e.currentTarget.style.backgroundColor =
                                        '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      isSelected ? '#f5f3ff' : 'transparent';
                                  }}
                                >
                                  <span>{option.label}</span>
                                  {isSelected && (
                                    <div
                                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: '#8b5cf6' }}
                                    >
                                      <Icon name="check" className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>

                        {/* Selection counter */}
                        {Array.isArray(localValues) && localValues.length > 0 && (
                          <div
                            className="px-3 py-1.5 text-[11px]"
                            style={{ color: '#9ca3af' }}
                          >
                            {localValues.length} selecionado
                            {localValues.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    )}

                    {/* ── Text Input ── */}
                    {currentFilterDef?.type === 'text' && (
                      <div className="p-3">
                        <input
                          type="text"
                          value={typeof localValues === 'string' ? localValues : ''}
                          onChange={(e) => setLocalValues(e.target.value)}
                          placeholder={currentFilterDef.placeholder}
                          className="w-full px-3 py-2 text-sm rounded-md border"
                          style={{
                            backgroundColor: '#ffffff',
                            borderColor: '#d1d5db',
                            color: '#1e293b',
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#8b5cf6';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#d1d5db';
                          }}
                          autoFocus
                        />
                      </div>
                    )}

                    {/* ── Date Range ── */}
                    {currentFilterDef?.type === 'daterange' && (
                      <div className="p-3 space-y-3">
                        <div>
                          <label
                            className="block text-xs font-medium mb-1"
                            style={{ color: '#64748b' }}
                          >
                            De
                          </label>
                          <input
                            type="date"
                            value={localDateFrom}
                            onChange={(e) => setLocalDateFrom(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#d1d5db',
                              color: '#1e293b',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                          />
                        </div>
                        <div>
                          <label
                            className="block text-xs font-medium mb-1"
                            style={{ color: '#64748b' }}
                          >
                            Até
                          </label>
                          <input
                            type="date"
                            value={localDateTo}
                            onChange={(e) => setLocalDateTo(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-md border"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#d1d5db',
                              color: '#1e293b',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Apply Footer */}
                  <div
                    className="px-3 py-2.5 flex items-center justify-end flex-shrink-0"
                    style={{ borderTop: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleApply}
                      className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
                      style={{
                        backgroundColor: '#8b5cf6',
                        color: '#ffffff',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#7c3aed';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#8b5cf6';
                      }}
                    >
                      Aplicar
                    </button>
                  </div>
                </>
              )}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

AdvancedFiltersBar.displayName = 'AdvancedFiltersBar';
